// src/components/floorplans/FloorPlanSVG.tsx
/**
 * SVG floor plan components for each unit type
 */

interface FloorPlanProps {
  unitType: string;
  sqft: number;
  className?: string;
}

export function FloorPlanSVG({ unitType, sqft, className }: FloorPlanProps) {
  switch (unitType) {
    case 'Bachelor':
      return <BachelorFloorPlan sqft={sqft} className={className} />;
    case 'Jr 1-BR':
      return <Junior1BRFloorPlan sqft={sqft} className={className} />;
    case '1-BR':
      return <OneBRFloorPlan sqft={sqft} className={className} />;
    case '1-BR+Den':
      return <OneBRDenFloorPlan sqft={sqft} className={className} />;
    case '2-BR':
      return <TwoBRFloorPlan sqft={sqft} className={className} />;
    default:
      return <OneBRFloorPlan sqft={sqft} className={className} />;
  }
}

interface PlanProps {
  sqft: number;
  className?: string;
}

// Bachelor Floor Plan (~380-450 sqft)
function BachelorFloorPlan({ sqft, className }: PlanProps) {
  return (
    <svg viewBox="0 0 300 250" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Outer walls */}
      <rect x="10" y="10" width="280" height="230" stroke="#374151" strokeWidth="3" fill="#f9fafb" />

      {/* Main living/sleeping area */}
      <rect x="10" y="10" width="200" height="170" stroke="#9ca3af" strokeWidth="1" fill="#ffffff" />
      <text x="110" y="95" textAnchor="middle" fontSize="12" fill="#6b7280">Living / Sleeping</text>
      <text x="110" y="110" textAnchor="middle" fontSize="10" fill="#9ca3af">15' x 12'</text>

      {/* Kitchen */}
      <rect x="210" y="10" width="80" height="100" stroke="#9ca3af" strokeWidth="1" fill="#fef3c7" />
      <text x="250" y="55" textAnchor="middle" fontSize="12" fill="#6b7280">Kitchen</text>
      <text x="250" y="70" textAnchor="middle" fontSize="10" fill="#9ca3af">8' x 9'</text>
      <rect x="215" y="15" width="70" height="8" fill="#d1d5db" />

      {/* Bathroom */}
      <rect x="210" y="110" width="80" height="70" stroke="#9ca3af" strokeWidth="1" fill="#dbeafe" />
      <text x="250" y="150" textAnchor="middle" fontSize="12" fill="#6b7280">Bath</text>
      <rect x="220" y="120" width="25" height="15" fill="#93c5fd" rx="2" />
      <circle cx="270" cy="165" r="8" fill="#93c5fd" />

      {/* Closet */}
      <rect x="10" y="180" width="60" height="60" stroke="#9ca3af" strokeWidth="1" fill="#f3f4f6" />
      <text x="40" y="215" textAnchor="middle" fontSize="10" fill="#6b7280">Closet</text>

      {/* Entry */}
      <rect x="70" y="180" width="50" height="60" stroke="#9ca3af" strokeWidth="1" fill="#ffffff" />
      <text x="95" y="215" textAnchor="middle" fontSize="10" fill="#6b7280">Entry</text>
      <rect x="85" y="235" width="20" height="5" fill="#374151" />

      {/* Balcony */}
      <rect x="120" y="180" width="90" height="60" stroke="#9ca3af" strokeWidth="1" strokeDasharray="4" fill="#ecfdf5" />
      <text x="165" y="215" textAnchor="middle" fontSize="10" fill="#6b7280">Balcony</text>

      {/* Square footage */}
      <text x="250" y="225" textAnchor="middle" fontSize="14" fontWeight="500" fill="#374151">{sqft} sq ft</text>
    </svg>
  );
}

// Junior 1-BR Floor Plan (~420-500 sqft)
function Junior1BRFloorPlan({ sqft, className }: PlanProps) {
  return (
    <svg viewBox="0 0 320 280" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="10" y="10" width="300" height="260" stroke="#374151" strokeWidth="3" fill="#f9fafb" />

      {/* Living room */}
      <rect x="10" y="10" width="150" height="140" stroke="#9ca3af" strokeWidth="1" fill="#ffffff" />
      <text x="85" y="75" textAnchor="middle" fontSize="12" fill="#6b7280">Living Room</text>
      <text x="85" y="90" textAnchor="middle" fontSize="10" fill="#9ca3af">12' x 11'</text>

      {/* Sleeping alcove */}
      <rect x="160" y="10" width="150" height="140" stroke="#9ca3af" strokeWidth="1" fill="#fef9c3" />
      <text x="235" y="75" textAnchor="middle" fontSize="12" fill="#6b7280">Sleeping Area</text>
      <text x="235" y="90" textAnchor="middle" fontSize="10" fill="#9ca3af">10' x 11'</text>
      <rect x="180" y="50" width="50" height="70" stroke="#d1d5db" strokeWidth="1" fill="#fef3c7" rx="2" />

      {/* Kitchen */}
      <rect x="10" y="150" width="120" height="80" stroke="#9ca3af" strokeWidth="1" fill="#fef3c7" />
      <text x="70" y="190" textAnchor="middle" fontSize="12" fill="#6b7280">Kitchen</text>
      <rect x="15" y="155" width="110" height="10" fill="#d1d5db" />

      {/* Bathroom */}
      <rect x="130" y="150" width="80" height="80" stroke="#9ca3af" strokeWidth="1" fill="#dbeafe" />
      <text x="170" y="195" textAnchor="middle" fontSize="12" fill="#6b7280">Bath</text>

      {/* Closet */}
      <rect x="210" y="150" width="50" height="80" stroke="#9ca3af" strokeWidth="1" fill="#f3f4f6" />
      <text x="235" y="195" textAnchor="middle" fontSize="10" fill="#6b7280">Closet</text>

      {/* Entry */}
      <rect x="260" y="150" width="50" height="80" stroke="#9ca3af" strokeWidth="1" fill="#ffffff" />
      <text x="285" y="195" textAnchor="middle" fontSize="10" fill="#6b7280">Entry</text>
      <rect x="275" y="225" width="20" height="5" fill="#374151" />

      {/* Balcony */}
      <rect x="10" y="230" width="120" height="40" stroke="#9ca3af" strokeWidth="1" strokeDasharray="4" fill="#ecfdf5" />
      <text x="70" y="255" textAnchor="middle" fontSize="10" fill="#6b7280">Balcony</text>

      <text x="260" y="255" textAnchor="middle" fontSize="14" fontWeight="500" fill="#374151">{sqft} sq ft</text>
    </svg>
  );
}

// 1-BR Floor Plan (~520-600 sqft)
function OneBRFloorPlan({ sqft, className }: PlanProps) {
  return (
    <svg viewBox="0 0 350 300" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="10" y="10" width="330" height="280" stroke="#374151" strokeWidth="3" fill="#f9fafb" />

      {/* Living room */}
      <rect x="10" y="10" width="180" height="150" stroke="#9ca3af" strokeWidth="1" fill="#ffffff" />
      <text x="100" y="80" textAnchor="middle" fontSize="12" fill="#6b7280">Living Room</text>
      <text x="100" y="95" textAnchor="middle" fontSize="10" fill="#9ca3af">14' x 12'</text>

      {/* Bedroom */}
      <rect x="190" y="10" width="150" height="150" stroke="#9ca3af" strokeWidth="1" fill="#fef9c3" />
      <text x="265" y="80" textAnchor="middle" fontSize="12" fill="#6b7280">Bedroom</text>
      <text x="265" y="95" textAnchor="middle" fontSize="10" fill="#9ca3af">12' x 12'</text>
      <rect x="210" y="40" width="60" height="80" stroke="#d1d5db" strokeWidth="1" fill="#fef3c7" rx="2" />
      <rect x="190" y="130" width="3" height="30" fill="#374151" />

      {/* Kitchen */}
      <rect x="10" y="160" width="130" height="90" stroke="#9ca3af" strokeWidth="1" fill="#fef3c7" />
      <text x="75" y="205" textAnchor="middle" fontSize="12" fill="#6b7280">Kitchen</text>
      <rect x="15" y="165" width="120" height="10" fill="#d1d5db" />
      <rect x="15" y="235" width="50" height="10" fill="#d1d5db" />

      {/* Dining */}
      <rect x="10" y="250" width="130" height="40" stroke="#9ca3af" strokeWidth="1" fill="#ffffff" />
      <text x="75" y="275" textAnchor="middle" fontSize="10" fill="#6b7280">Dining</text>

      {/* Bathroom */}
      <rect x="140" y="160" width="90" height="70" stroke="#9ca3af" strokeWidth="1" fill="#dbeafe" />
      <text x="185" y="200" textAnchor="middle" fontSize="12" fill="#6b7280">Bathroom</text>
      <rect x="150" y="170" width="30" height="50" fill="#93c5fd" rx="2" />

      {/* Closet */}
      <rect x="230" y="160" width="50" height="70" stroke="#9ca3af" strokeWidth="1" fill="#f3f4f6" />
      <text x="255" y="200" textAnchor="middle" fontSize="10" fill="#6b7280">Closet</text>

      {/* Entry */}
      <rect x="280" y="160" width="60" height="70" stroke="#9ca3af" strokeWidth="1" fill="#ffffff" />
      <text x="310" y="200" textAnchor="middle" fontSize="10" fill="#6b7280">Entry</text>
      <rect x="300" y="225" width="20" height="5" fill="#374151" />

      {/* Balcony */}
      <rect x="140" y="230" width="200" height="60" stroke="#9ca3af" strokeWidth="1" strokeDasharray="4" fill="#ecfdf5" />
      <text x="240" y="265" textAnchor="middle" fontSize="12" fill="#6b7280">Balcony</text>

      <text x="75" y="268" textAnchor="middle" fontSize="14" fontWeight="500" fill="#374151">{sqft} sq ft</text>
    </svg>
  );
}

// 1-BR + Den Floor Plan (~650-750 sqft)
function OneBRDenFloorPlan({ sqft, className }: PlanProps) {
  return (
    <svg viewBox="0 0 380 320" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="10" y="10" width="360" height="300" stroke="#374151" strokeWidth="3" fill="#f9fafb" />

      {/* Living room */}
      <rect x="10" y="10" width="160" height="140" stroke="#9ca3af" strokeWidth="1" fill="#ffffff" />
      <text x="90" y="75" textAnchor="middle" fontSize="12" fill="#6b7280">Living Room</text>
      <text x="90" y="90" textAnchor="middle" fontSize="10" fill="#9ca3af">13' x 11'</text>

      {/* Den */}
      <rect x="170" y="10" width="100" height="100" stroke="#9ca3af" strokeWidth="1" fill="#e0e7ff" />
      <text x="220" y="55" textAnchor="middle" fontSize="12" fill="#6b7280">Den</text>
      <text x="220" y="70" textAnchor="middle" fontSize="10" fill="#9ca3af">8' x 8'</text>

      {/* Bedroom */}
      <rect x="270" y="10" width="100" height="150" stroke="#9ca3af" strokeWidth="1" fill="#fef9c3" />
      <text x="320" y="80" textAnchor="middle" fontSize="12" fill="#6b7280">Bedroom</text>
      <text x="320" y="95" textAnchor="middle" fontSize="10" fill="#9ca3af">11' x 12'</text>
      <rect x="285" y="40" width="55" height="75" stroke="#d1d5db" strokeWidth="1" fill="#fef3c7" rx="2" />

      {/* Kitchen */}
      <rect x="10" y="150" width="140" height="100" stroke="#9ca3af" strokeWidth="1" fill="#fef3c7" />
      <text x="80" y="200" textAnchor="middle" fontSize="12" fill="#6b7280">Kitchen</text>
      <rect x="15" y="155" width="130" height="10" fill="#d1d5db" />

      {/* Bathroom */}
      <rect x="150" y="110" width="80" height="80" stroke="#9ca3af" strokeWidth="1" fill="#dbeafe" />
      <text x="190" y="155" textAnchor="middle" fontSize="12" fill="#6b7280">Bath</text>

      {/* Walk-in Closet */}
      <rect x="230" y="160" width="60" height="80" stroke="#9ca3af" strokeWidth="1" fill="#f3f4f6" />
      <text x="260" y="205" textAnchor="middle" fontSize="10" fill="#6b7280">W.I.C.</text>

      {/* Entry */}
      <rect x="290" y="160" width="80" height="80" stroke="#9ca3af" strokeWidth="1" fill="#ffffff" />
      <text x="330" y="205" textAnchor="middle" fontSize="10" fill="#6b7280">Entry</text>
      <rect x="320" y="235" width="20" height="5" fill="#374151" />

      {/* Balcony */}
      <rect x="10" y="250" width="280" height="60" stroke="#9ca3af" strokeWidth="1" strokeDasharray="4" fill="#ecfdf5" />
      <text x="150" y="285" textAnchor="middle" fontSize="12" fill="#6b7280">Balcony</text>

      <text x="330" y="280" textAnchor="middle" fontSize="14" fontWeight="500" fill="#374151">{sqft} sq ft</text>
    </svg>
  );
}

// 2-BR Floor Plan (~750-900 sqft)
function TwoBRFloorPlan({ sqft, className }: PlanProps) {
  return (
    <svg viewBox="0 0 420 340" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="10" y="10" width="400" height="320" stroke="#374151" strokeWidth="3" fill="#f9fafb" />

      {/* Living room */}
      <rect x="10" y="10" width="180" height="150" stroke="#9ca3af" strokeWidth="1" fill="#ffffff" />
      <text x="100" y="80" textAnchor="middle" fontSize="12" fill="#6b7280">Living Room</text>
      <text x="100" y="95" textAnchor="middle" fontSize="10" fill="#9ca3af">15' x 12'</text>

      {/* Master Bedroom */}
      <rect x="190" y="10" width="120" height="130" stroke="#9ca3af" strokeWidth="1" fill="#fef9c3" />
      <text x="250" y="70" textAnchor="middle" fontSize="12" fill="#6b7280">Master Bed</text>
      <text x="250" y="85" textAnchor="middle" fontSize="10" fill="#9ca3af">12' x 11'</text>
      <rect x="205" y="35" width="55" height="70" stroke="#d1d5db" strokeWidth="1" fill="#fef3c7" rx="2" />

      {/* Second Bedroom */}
      <rect x="310" y="10" width="100" height="130" stroke="#9ca3af" strokeWidth="1" fill="#fef9c3" />
      <text x="360" y="70" textAnchor="middle" fontSize="12" fill="#6b7280">Bedroom 2</text>
      <text x="360" y="85" textAnchor="middle" fontSize="10" fill="#9ca3af">10' x 11'</text>
      <rect x="325" y="40" width="45" height="60" stroke="#d1d5db" strokeWidth="1" fill="#fef3c7" rx="2" />

      {/* Kitchen */}
      <rect x="10" y="160" width="130" height="100" stroke="#9ca3af" strokeWidth="1" fill="#fef3c7" />
      <text x="75" y="210" textAnchor="middle" fontSize="12" fill="#6b7280">Kitchen</text>
      <rect x="15" y="165" width="120" height="10" fill="#d1d5db" />
      <rect x="15" y="245" width="60" height="10" fill="#d1d5db" />

      {/* Dining */}
      <rect x="10" y="260" width="130" height="70" stroke="#9ca3af" strokeWidth="1" fill="#ffffff" />
      <text x="75" y="300" textAnchor="middle" fontSize="10" fill="#6b7280">Dining</text>

      {/* Main Bathroom */}
      <rect x="140" y="160" width="80" height="80" stroke="#9ca3af" strokeWidth="1" fill="#dbeafe" />
      <text x="180" y="205" textAnchor="middle" fontSize="12" fill="#6b7280">Bath</text>

      {/* Ensuite Bathroom */}
      <rect x="190" y="140" width="60" height="60" stroke="#9ca3af" strokeWidth="1" fill="#dbeafe" />
      <text x="220" y="175" textAnchor="middle" fontSize="10" fill="#6b7280">Ensuite</text>

      {/* Master Closet */}
      <rect x="250" y="140" width="60" height="60" stroke="#9ca3af" strokeWidth="1" fill="#f3f4f6" />
      <text x="280" y="175" textAnchor="middle" fontSize="10" fill="#6b7280">Closet</text>

      {/* Second Closet */}
      <rect x="310" y="140" width="50" height="60" stroke="#9ca3af" strokeWidth="1" fill="#f3f4f6" />
      <text x="335" y="175" textAnchor="middle" fontSize="10" fill="#6b7280">Closet</text>

      {/* Entry */}
      <rect x="360" y="140" width="50" height="120" stroke="#9ca3af" strokeWidth="1" fill="#ffffff" />
      <text x="385" y="205" textAnchor="middle" fontSize="10" fill="#6b7280">Entry</text>
      <rect x="375" y="255" width="20" height="5" fill="#374151" />

      {/* Balcony */}
      <rect x="140" y="260" width="220" height="70" stroke="#9ca3af" strokeWidth="1" strokeDasharray="4" fill="#ecfdf5" />
      <text x="250" y="300" textAnchor="middle" fontSize="12" fill="#6b7280">Balcony</text>

      <text x="385" y="300" textAnchor="middle" fontSize="14" fontWeight="500" fill="#374151">{sqft} sq ft</text>
    </svg>
  );
}

export function FloorPlanLegend() {
  return (
    <div className="flex flex-wrap gap-4 text-xs text-gray-600 mt-4 justify-center">
      <div className="flex items-center gap-1">
        <div className="w-3 h-3 bg-white border border-gray-300"></div>
        <span>Living</span>
      </div>
      <div className="flex items-center gap-1">
        <div className="w-3 h-3 bg-yellow-50 border border-gray-300"></div>
        <span>Bedroom</span>
      </div>
      <div className="flex items-center gap-1">
        <div className="w-3 h-3 bg-amber-100 border border-gray-300"></div>
        <span>Kitchen</span>
      </div>
      <div className="flex items-center gap-1">
        <div className="w-3 h-3 bg-blue-100 border border-gray-300"></div>
        <span>Bathroom</span>
      </div>
      <div className="flex items-center gap-1">
        <div className="w-3 h-3 bg-green-50 border border-gray-300 border-dashed"></div>
        <span>Balcony</span>
      </div>
    </div>
  );
}
